import React, { useState, useEffect, useMemo } from 'react';
import { format, subDays, startOfMonth, endOfMonth, subMonths, isToday } from 'date-fns';
import { 
  TrendingUp, 
  Calendar,
  RefreshCw,
  Loader2,
  ChevronDown,
  ChevronUp,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  BarChart3,
  X
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Calendar as CalendarComponent } from '../ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import axios from 'axios';
import { PLATFORM_CHART_COLORS, getPlatformBadgeClass, getPlatformName } from './constants';

const API = process.env.REACT_APP_BACKEND_URL;

const formatNumber = (num) => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num?.toString() || '0';
};

const formatDateShort = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return format(date, 'MM/dd');
};

// Translations
const TEXTS = {
  en: {
    title: 'Follower Growth',
    subtitle: 'Track follower changes across all accounts',
    quickSelect: 'Quick Select',
    last7Days: 'Last 7 days',
    last30Days: 'Last 30 days',
    last90Days: 'Last 90 days',
    thisMonth: 'This month',
    lastMonth: 'Last month',
    allTime: 'All time',
    startDate: 'Start',
    endDate: 'End',
    account: 'Account',
    currentFollowers: 'Current',
    periodStart: 'Period Start',
    change: 'Change',
    noData: 'No snapshot data available. Click "Create Snapshot" to start tracking.',
    refresh: 'Refresh',
    createSnapshot: 'Create Snapshot',
    snapshotCreated: 'Snapshots created successfully',
    comparisonTable: 'Account Comparison',
    dailyDetails: 'Daily Changes',
    date: 'Date',
    followers: 'Followers',
    dailyChange: 'Daily Change',
    hideDetails: 'Hide',
    showDetails: 'Details',
    today: 'Today',
    growthRate: 'Growth Rate',
  },
  ko: {
    title: '팔로워 증감 추이',
    subtitle: '모든 계정의 팔로워 변화를 추적합니다',
    quickSelect: '빠른 선택',
    last7Days: '최근 7일',
    last30Days: '최근 30일',
    last90Days: '최근 90일',
    thisMonth: '이번 달',
    lastMonth: '지난 달',
    allTime: '전체',
    startDate: '시작일',
    endDate: '종료일',
    account: '계정',
    currentFollowers: '현재',
    periodStart: '기간 시작',
    change: '증감',
    noData: '스냅샷 데이터가 없습니다. "스냅샷 생성"을 클릭하여 추적을 시작하세요.',
    refresh: '새로고침',
    createSnapshot: '스냅샷 생성',
    snapshotCreated: '스냅샷이 생성되었습니다',
    comparisonTable: '계정별 비교',
    dailyDetails: '일별 변화',
    date: '날짜',
    followers: '팔로워',
    dailyChange: '일일 변화',
    hideDetails: '닫기',
    showDetails: '상세',
    today: '오늘',
    growthRate: '증감률',
  },
  zh: {
    title: '粉丝增长',
    subtitle: '追踪所有账户的粉丝变化',
    quickSelect: '快速选择',
    last7Days: '最近7天',
    last30Days: '最近30天',
    last90Days: '最近90天',
    thisMonth: '本月',
    lastMonth: '上月',
    allTime: '全部',
    startDate: '开始',
    endDate: '结束',
    account: '账户',
    currentFollowers: '当前',
    periodStart: '期初',
    change: '变化',
    noData: '暂无快照数据，点击"创建快照"开始追踪。',
    refresh: '刷新',
    createSnapshot: '创建快照',
    snapshotCreated: '快照创建成功',
    comparisonTable: '账户对比',
    dailyDetails: '每日变化',
    date: '日期',
    followers: '粉丝',
    dailyChange: '日变化',
    hideDetails: '隐藏',
    showDetails: '详情',
    today: '今天',
    growthRate: '增长率',
  }
};

// Daily Detail Panel Component
const DailyDetailPanel = ({ account, lang, onClose }) => {
  const t = TEXTS[lang] || TEXTS.en;
  const dailyChanges = account.daily_changes || [];
  
  // Reverse to show most recent first
  const sortedChanges = [...dailyChanges].reverse();
  
  return (
    <tr>
      <td colSpan={5} className="p-0">
        <div className="bg-muted/20 border-t border-b border-border/50 p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-primary" />
              {account.display_name} - {t.dailyDetails}
            </h4>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Mini chart for this account */}
          <div className="h-40 mb-4">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={sortedChanges.slice().reverse()}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={formatDateShort}
                  stroke="hsl(var(--muted-foreground))" 
                  fontSize={10}
                />
                <YAxis 
                  stroke="hsl(var(--muted-foreground))" 
                  fontSize={10}
                  tickFormatter={(v) => formatNumber(v)}
                  domain={['auto', 'auto']}
                />
                <Tooltip 
                  formatter={(value) => [formatNumber(value), t.followers]}
                  labelFormatter={(label) => label}
                />
                <Line
                  type="monotone"
                  dataKey="followers"
                  stroke={PLATFORM_CHART_COLORS[account.platform] || '#8884d8'}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          
          {/* Daily changes table */}
          <div className="max-h-64 overflow-y-auto rounded-md border border-border/50">
            <table className="w-full text-xs">
              <thead className="bg-muted/50 sticky top-0">
                <tr>
                  <th className="text-left py-2 px-3 font-medium">{t.date}</th>
                  <th className="text-right py-2 px-3 font-medium">{t.followers}</th>
                  <th className="text-right py-2 px-3 font-medium">{t.dailyChange}</th>
                </tr>
              </thead>
              <tbody>
                {sortedChanges.map((day, i) => (
                  <tr key={i} className="border-t border-border/30 hover:bg-muted/30">
                    <td className="py-2 px-3 font-mono">{day.date}</td>
                    <td className="py-2 px-3 text-right font-mono">{formatNumber(day.followers)}</td>
                    <td className="py-2 px-3 text-right">
                      <div className={`flex items-center justify-end gap-1 ${
                        day.daily_delta > 0 ? 'text-emerald-500' : 
                        day.daily_delta < 0 ? 'text-red-500' : 'text-muted-foreground'
                      }`}>
                        {day.daily_delta > 0 ? <ArrowUpRight className="h-3 w-3" /> : 
                         day.daily_delta < 0 ? <ArrowDownRight className="h-3 w-3" /> : 
                         <Minus className="h-3 w-3" />}
                        <span className="font-mono">
                          {day.daily_delta > 0 ? '+' : ''}{formatNumber(day.daily_delta)}
                        </span>
                        <span className="text-muted-foreground">
                          ({day.daily_delta_percent > 0 ? '+' : ''}{day.daily_delta_percent}%)
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
                {sortedChanges.length === 0 && (
                  <tr>
                    <td colSpan={3} className="py-4 text-center text-muted-foreground">
                      {t.noData}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </td>
    </tr>
  );
};

export default function FollowerGrowthPage({ lang = 'en' }) {
  const t = useMemo(() => TEXTS[lang] || TEXTS.en, [lang]);
  
  // Date state
  const today = useMemo(() => new Date(), []);
  const [startDate, setStartDate] = useState(subDays(today, 7));
  const [endDate, setEndDate] = useState(today);
  const [quickSelectValue, setQuickSelectValue] = useState('7d');
  
  // Data state
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  
  // UI state
  const [expandedAccount, setExpandedAccount] = useState(null);

  // Quick select options
  const quickSelectOptions = useMemo(() => [
    { value: '7d', label: t.last7Days, getDates: () => ({ start: subDays(today, 7), end: today }) },
    { value: '30d', label: t.last30Days, getDates: () => ({ start: subDays(today, 30), end: today }) },
    { value: '90d', label: t.last90Days, getDates: () => ({ start: subDays(today, 90), end: today }) },
    { value: 'thisMonth', label: t.thisMonth, getDates: () => ({ start: startOfMonth(today), end: today }) },
    { value: 'lastMonth', label: t.lastMonth, getDates: () => ({ start: startOfMonth(subMonths(today, 1)), end: endOfMonth(subMonths(today, 1)) }) },
    { value: 'all', label: t.allTime, getDates: () => ({ start: null, end: null }) },
  ], [t, today]);

  const handleQuickSelect = (value) => {
    setQuickSelectValue(value);
    const option = quickSelectOptions.find(o => o.value === value);
    if (option) {
      const { start, end } = option.getDates();
      setStartDate(start);
      setEndDate(end);
    }
  };

  const handleDateChange = (type, date) => {
    if (type === 'start') {
      setStartDate(date);
    } else {
      setEndDate(date);
    }
    setQuickSelectValue('custom');
  };

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `${API}/api/follower-growth?`;
      
      if (startDate && endDate) {
        url += `range=custom&start_date=${format(startDate, 'yyyy-MM-dd')}&end_date=${format(endDate, 'yyyy-MM-dd')}`;
      } else {
        url += 'range=all';
      }
      
      const res = await axios.get(url);
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch follower growth:', err);
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSnapshot = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/api/snapshots/create-all`);
      await fetchData();
    } catch (err) {
      console.error('Failed to create snapshots:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const renderDeltaIndicator = (delta, percent) => {
    if (delta > 0) {
      return (
        <div className="flex items-center gap-1 text-emerald-500">
          <ArrowUpRight className="h-4 w-4" />
          <span className="font-mono">+{formatNumber(delta)}</span>
          <span className="text-xs text-emerald-500/70">({percent > 0 ? '+' : ''}{percent}%)</span>
        </div>
      );
    } else if (delta < 0) {
      return (
        <div className="flex items-center gap-1 text-red-500">
          <ArrowDownRight className="h-4 w-4" />
          <span className="font-mono">{formatNumber(delta)}</span>
          <span className="text-xs text-red-500/70">({percent}%)</span>
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-1 text-muted-foreground">
          <Minus className="h-4 w-4" />
          <span className="font-mono">0</span>
          <span className="text-xs">(0%)</span>
        </div>
      );
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <TrendingUp className="h-6 w-6 text-primary" />
            {t.title}
          </h2>
          <p className="text-muted-foreground mt-1">{t.subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t.refresh}
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={handleCreateSnapshot}
            disabled={refreshing}
          >
            {refreshing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Calendar className="h-4 w-4 mr-2" />
            )}
            {t.createSnapshot}
          </Button>
        </div>
      </div>

      {/* Date Filters */}
      <Card className="bg-card border-border/50">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-3">
            {/* Quick Select */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{t.quickSelect}:</span>
              <Select value={quickSelectValue} onValueChange={handleQuickSelect}>
                <SelectTrigger className="w-[140px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {quickSelectOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <span className="text-muted-foreground hidden sm:inline">|</span>

            {/* Start Date */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-9">
                  <Calendar className="h-4 w-4 mr-2" />
                  {startDate ? (
                    isToday(startDate) ? t.today : format(startDate, 'yyyy-MM-dd')
                  ) : t.startDate}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={startDate}
                  onSelect={(date) => handleDateChange('start', date)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            <span className="text-muted-foreground">~</span>

            {/* End Date */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-9">
                  <Calendar className="h-4 w-4 mr-2" />
                  {endDate ? (
                    isToday(endDate) ? t.today : format(endDate, 'yyyy-MM-dd')
                  ) : t.endDate}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={endDate}
                  onSelect={(date) => handleDateChange('end', date)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </CardContent>
      </Card>

      {/* Comparison Table - Main View */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-lg font-heading">{t.comparisonTable}</CardTitle>
        </CardHeader>
        <CardContent>
          {data?.accounts?.length > 0 ? (
            <>
              {/* Mobile: Card Layout */}
              <div className="md:hidden space-y-3">
                {data?.accounts?.map((acc, i) => (
                  <React.Fragment key={acc.account_id}>
                    <div className="p-3 rounded-lg border border-border/30 space-y-2" data-testid={`account-row-${i}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <Badge variant="outline" className={`${getPlatformBadgeClass(acc.platform)} text-xs capitalize shrink-0`}>
                            {getPlatformName(acc.platform, lang)}
                          </Badge>
                          <span className="font-medium text-foreground text-sm truncate">{acc.display_name}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">{t.currentFollowers}</span>
                          <span className="font-mono font-medium">{formatNumber(acc.current_followers)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">{t.periodStart}</span>
                          <span className="font-mono text-muted-foreground">{formatNumber(acc.oldest_followers)}</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-1 border-t border-border/20">
                        <div className="text-xs">
                          {renderDeltaIndicator(acc.follower_delta, acc.follower_delta_percent)}
                        </div>
                        <Button
                          variant={expandedAccount === acc.account_id ? "secondary" : "ghost"}
                          size="sm"
                          onClick={() => setExpandedAccount(expandedAccount === acc.account_id ? null : acc.account_id)}
                          className="h-7 px-2 text-xs"
                        >
                          {expandedAccount === acc.account_id ? (
                            <><ChevronUp className="h-3 w-3 mr-1" />{t.hideDetails}</>
                          ) : (
                            <><ChevronDown className="h-3 w-3 mr-1" />{t.showDetails}</>
                          )}
                        </Button>
                      </div>
                    </div>
                    {expandedAccount === acc.account_id && (
                      <DailyDetailPanel account={acc} lang={lang} onClose={() => setExpandedAccount(null)} />
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Desktop: Table Layout */}
              <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">{t.account}</th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground">{t.currentFollowers}</th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground">{t.periodStart}</th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground">{t.change}</th>
                    <th className="text-center py-3 px-4 font-medium text-muted-foreground w-24">{t.dailyDetails}</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.accounts?.map((acc, i) => (
                    <React.Fragment key={acc.account_id}>
                      <tr 
                        className={`border-b border-border/50 hover:bg-muted/30 transition-colors ${
                          expandedAccount === acc.account_id ? 'bg-muted/20' : ''
                        }`}
                        data-testid={`account-row-${i}`}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Badge 
                              variant="outline" 
                              className={`${getPlatformBadgeClass(acc.platform)} text-xs capitalize`}
                            >
                              {getPlatformName(acc.platform, lang)}
                            </Badge>
                            <span className="font-medium text-foreground">{acc.display_name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-foreground">
                          {formatNumber(acc.current_followers)}
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-muted-foreground">
                          {formatNumber(acc.oldest_followers)}
                        </td>
                        <td className="py-3 px-4 text-right">
                          {renderDeltaIndicator(acc.follower_delta, acc.follower_delta_percent)}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Button
                            variant={expandedAccount === acc.account_id ? "secondary" : "ghost"}
                            size="sm"
                            onClick={() => setExpandedAccount(
                              expandedAccount === acc.account_id ? null : acc.account_id
                            )}
                            className="h-8 px-3"
                          >
                            {expandedAccount === acc.account_id ? (
                              <>
                                <ChevronUp className="h-4 w-4 mr-1" />
                                {t.hideDetails}
                              </>
                            ) : (
                              <>
                                <ChevronDown className="h-4 w-4 mr-1" />
                                {t.showDetails}
                              </>
                            )}
                          </Button>
                        </td>
                      </tr>
                      
                      {/* Expanded Daily Details */}
                      {expandedAccount === acc.account_id && (
                        <DailyDetailPanel 
                          account={acc} 
                          lang={lang}
                          onClose={() => setExpandedAccount(null)}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          ) : (
            <div className="py-12 text-center">
              <Calendar className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground mb-4">{t.noData}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCreateSnapshot}
                disabled={refreshing}
              >
                {refreshing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {t.createSnapshot}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
